/**
 * StandardsDiffModal — diff view between two dev-standards versions.
 *
 * Uses the `diff` library (jsdiff) for line-level diffing rendered with
 * design-token CSS variables only (no hardcoded colors).
 *
 * Strategy: client-side two-fetch approach (no BE diff endpoint needed).
 * Each full document is fetched via useStandardsVersion(id) — cached by
 * TanStack Query, so the second open is instant.
 */
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as Diff from 'diff';
import { useStandardsVersion } from '../hooks/useStandardsVersion';
import type { DevStandardsSummary } from '../types/devStandards';

/* ─── Types ──────────────────────────────────────────────────────────────── */

type DiffMode = 'unified' | 'sidebyside';

interface SectionChange {
  heading: string;
  status: 'added' | 'removed' | 'changed' | 'same';
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */

/** Extract all top-level ## headings from markdown text */
function extractH2Headings(text: string): string[] {
  return (text.match(/^## .+/gm) ?? []).map((h) => h.replace(/^## /, '').trim());
}

/**
 * Compare heading sets + the content blocks under each heading.
 * Returns a compact summary list.
 */
function computeSectionChanges(fromText: string, toText: string): SectionChange[] {
  const fromHeadings = new Set(extractH2Headings(fromText));
  const toHeadings = new Set(extractH2Headings(toText));
  const all = new Set([...fromHeadings, ...toHeadings]);

  const result: SectionChange[] = [];

  for (const h of all) {
    if (!fromHeadings.has(h)) {
      result.push({ heading: h, status: 'added' });
    } else if (!toHeadings.has(h)) {
      result.push({ heading: h, status: 'removed' });
    } else {
      // both have it — compare the content block under this heading
      const fromBlock = extractSectionBlock(fromText, h);
      const toBlock = extractSectionBlock(toText, h);
      result.push({ heading: h, status: fromBlock === toBlock ? 'same' : 'changed' });
    }
  }

  return result;
}

/** Extract the text under a given ## heading until the next ## */
function extractSectionBlock(text: string, heading: string): string {
  const escapedHeading = heading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`^## ${escapedHeading}[\\s\\S]*?(?=^## |$)`, 'm');
  const match = text.match(regex);
  return match ? match[0] : '';
}

/** Format a date+version string for modal header */
function formatLabel(version: DevStandardsSummary | undefined, locale: string): string {
  if (!version) return '…';
  const date = new Intl.DateTimeFormat(locale === 'en' ? 'en-US' : 'ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(version.exportedAt));
  return `${date} (${version.docVersion})`;
}

/* ─── Sub-components ─────────────────────────────────────────────────────── */

function SectionChangeSummary({ changes }: { changes: SectionChange[] }) {
  const { t } = useTranslation();
  const notable = changes.filter((c) => c.status !== 'same');

  if (notable.length === 0) {
    return (
      <p
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-muted)',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        {t('devStandardsViewer.diff.noSectionChanges')}
      </p>
    );
  }

  return (
    <div
      aria-label={t('devStandardsViewer.diff.sectionSummary')}
      style={{
        marginBottom: 'var(--spacing-4)',
        padding: 'var(--spacing-3)',
        background: 'var(--color-surface-muted)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-surface-border)',
      }}
    >
      <p
        style={{
          fontSize: 'var(--font-size-xs)',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          marginBottom: 'var(--spacing-2)',
          fontFamily: 'var(--font-family-sans)',
        }}
      >
        {t('devStandardsViewer.diff.sectionSummary')}
      </p>
      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-1)',
        }}
      >
        {notable.map((c) => {
          const prefix =
            c.status === 'added' ? '+ ' : c.status === 'removed' ? '− ' : '~ ';
          const labelKey =
            c.status === 'added'
              ? 'devStandardsViewer.diff.added'
              : c.status === 'removed'
              ? 'devStandardsViewer.diff.removed'
              : 'devStandardsViewer.diff.changed';
          const color =
            c.status === 'added'
              ? 'var(--color-success)'
              : c.status === 'removed'
              ? 'var(--color-danger)'
              : 'var(--color-warning)';

          return (
            <li
              key={c.heading}
              style={{
                fontSize: 'var(--font-size-xs)',
                fontFamily: 'var(--font-family-sans)',
                color,
              }}
            >
              <span style={{ fontFamily: 'var(--font-family-mono)' }}>{prefix}</span>
              {c.heading}{' '}
              <span style={{ color: 'var(--color-text-muted)' }}>({t(labelKey)})</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

interface UnifiedDiffViewProps {
  fromText: string;
  toText: string;
}

function UnifiedDiffView({ fromText, toText }: UnifiedDiffViewProps) {
  const { t } = useTranslation();
  const hunks = useMemo(() => Diff.diffLines(fromText, toText), [fromText, toText]);

  const hasChanges = hunks.some((h) => h.added || h.removed);

  if (!hasChanges) {
    return (
      <p
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-muted)',
          textAlign: 'center',
          padding: 'var(--spacing-8)',
        }}
      >
        {t('devStandardsViewer.diff.identical')}
      </p>
    );
  }

  return (
    <pre
      role="region"
      aria-label={t('devStandardsViewer.diff.unifiedView')}
      style={{
        fontFamily: 'var(--font-family-mono)',
        fontSize: 'var(--font-size-xs)',
        lineHeight: 1.5,
        margin: 0,
        overflowX: 'auto',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
      }}
    >
      {hunks.map((part, i) => {
        const bg = part.added
          ? 'var(--color-diff-added-bg, color-mix(in srgb, var(--color-success) 12%, var(--color-surface-background)))'
          : part.removed
          ? 'var(--color-diff-removed-bg, color-mix(in srgb, var(--color-danger) 12%, var(--color-surface-background)))'
          : 'var(--color-surface-background)';
        const textColor = part.added
          ? 'var(--color-success)'
          : part.removed
          ? 'var(--color-danger)'
          : 'var(--color-text-primary)';
        const prefix = part.added ? '+' : part.removed ? '-' : ' ';
        const lines = (part.value ?? '').split('\n');
        // jsdiff includes a trailing empty string from split — trim
        if (lines[lines.length - 1] === '') lines.pop();

        return lines.map((line, j) => (
          <div
            key={`${i}-${j}`}
            style={{
              background: bg,
              color: textColor,
              padding: '0 var(--spacing-3)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            <span style={{ opacity: 0.5, userSelect: 'none', marginRight: 'var(--spacing-2)' }}>
              {prefix}
            </span>
            {line}
          </div>
        ));
      })}
    </pre>
  );
}

interface SideBySideDiffViewProps {
  fromText: string;
  toText: string;
}

function SideBySideDiffView({ fromText, toText }: SideBySideDiffViewProps) {
  const { t } = useTranslation();
  const fromLines = useMemo(() => fromText.split('\n'), [fromText]);
  const toLines = useMemo(() => toText.split('\n'), [toText]);

  // Build line-level alignment using jsdiff
  const hunks = useMemo(() => Diff.diffLines(fromText, toText), [fromText, toText]);

  const hasChanges = hunks.some((h) => h.added || h.removed);

  if (!hasChanges) {
    return (
      <p
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-muted)',
          textAlign: 'center',
          padding: 'var(--spacing-8)',
        }}
      >
        {t('devStandardsViewer.diff.identical')}
      </p>
    );
  }

  // Build aligned rows: {left, right, leftChanged, rightChanged}
  type Row = { left: string; right: string; leftRemoved: boolean; rightAdded: boolean };
  const rows: Row[] = [];
  let lIdx = 0;
  let rIdx = 0;

  for (const part of hunks) {
    const partLines = (part.value ?? '').split('\n');
    if (partLines[partLines.length - 1] === '') partLines.pop();
    const count = partLines.length;

    if (!part.added && !part.removed) {
      // unchanged — show on both sides
      for (let i = 0; i < count; i++) {
        rows.push({ left: fromLines[lIdx++] ?? '', right: toLines[rIdx++] ?? '', leftRemoved: false, rightAdded: false });
      }
    } else if (part.removed) {
      // removed from left — pair with empty right (will be filled by following added block)
      for (let i = 0; i < count; i++) {
        rows.push({ left: fromLines[lIdx++] ?? '', right: '', leftRemoved: true, rightAdded: false });
      }
    } else if (part.added) {
      // added on right — try to align with existing empty-right rows at end
      let paired = 0;
      for (let i = rows.length - 1; i >= 0 && rows[i].right === '' && rows[i].leftRemoved && paired < count; i--, paired++) {
        rows[i].right = toLines[rIdx++] ?? '';
        rows[i].rightAdded = true;
      }
      // any remaining added lines get their own row
      for (let i = paired; i < count; i++) {
        rows.push({ left: '', right: toLines[rIdx++] ?? '', leftRemoved: false, rightAdded: true });
      }
    }
  }

  const cellStyle = (changed: boolean, side: 'left' | 'right'): React.CSSProperties => ({
    flex: 1,
    padding: '1px var(--spacing-3)',
    fontFamily: 'var(--font-family-mono)',
    fontSize: 'var(--font-size-xs)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    lineHeight: 1.5,
    background: changed
      ? side === 'left'
        ? 'color-mix(in srgb, var(--color-danger) 12%, var(--color-surface-background))'
        : 'color-mix(in srgb, var(--color-success) 12%, var(--color-surface-background))'
      : 'var(--color-surface-background)',
    color: changed
      ? side === 'left'
        ? 'var(--color-danger)'
        : 'var(--color-success)'
      : 'var(--color-text-primary)',
    borderRight: side === 'left' ? '1px solid var(--color-surface-border)' : undefined,
  });

  return (
    <div
      role="region"
      aria-label={t('devStandardsViewer.diff.sideBySideView')}
      style={{
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        overflowX: 'auto',
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          background: 'var(--color-surface-muted)',
          borderBottom: '1px solid var(--color-surface-border)',
        }}
      >
        <div style={{ flex: 1, padding: 'var(--spacing-2) var(--spacing-3)', fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', borderRight: '1px solid var(--color-surface-border)', fontFamily: 'var(--font-family-sans)' }}>
          {t('devStandardsViewer.diff.from')}
        </div>
        <div style={{ flex: 1, padding: 'var(--spacing-2) var(--spacing-3)', fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-sans)' }}>
          {t('devStandardsViewer.diff.to')}
        </div>
      </div>
      {rows.map((row, i) => (
        <div key={i} style={{ display: 'flex', minHeight: '1.5em' }}>
          <div style={cellStyle(row.leftRemoved, 'left')}>{row.left}</div>
          <div style={cellStyle(row.rightAdded, 'right')}>{row.right}</div>
        </div>
      ))}
    </div>
  );
}

/* ─── Main modal ─────────────────────────────────────────────────────────── */

export interface StandardsDiffModalProps {
  /** The "from" version id (typically the older / non-active version) */
  fromId: number;
  /** The "to" version id (typically the active / newer version) */
  toId: number;
  /** Summary list — used for labels in modal header */
  versions: DevStandardsSummary[];
  onClose: () => void;
}

export function StandardsDiffModal({
  fromId,
  toId,
  versions,
  onClose,
}: StandardsDiffModalProps) {
  const { t, i18n } = useTranslation();
  const [mode, setMode] = useState<DiffMode>('unified');

  const fromQuery = useStandardsVersion(fromId);
  const toQuery = useStandardsVersion(toId);

  const fromVersion = versions.find((v) => v.id === fromId);
  const toVersion = versions.find((v) => v.id === toId);

  const fromLabel = formatLabel(fromVersion, i18n.language);
  const toLabel = formatLabel(toVersion, i18n.language);

  const sectionChanges = useMemo(() => {
    if (!fromQuery.data || !toQuery.data) return null;
    return computeSectionChanges(fromQuery.data.markdownText, toQuery.data.markdownText);
  }, [fromQuery.data, toQuery.data]);

  // Close on Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const isLoading = fromQuery.isLoading || toQuery.isLoading;
  const isError = fromQuery.isError || toQuery.isError;

  return (
    /* Backdrop */
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('devStandardsViewer.diff.modalTitle', { from: fromLabel, to: toLabel })}
      aria-describedby="diff-modal-body"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1200,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        overflowY: 'auto',
        padding: 'var(--spacing-8) var(--spacing-4)',
      }}
    >
      {/* Modal panel */}
      <div
        style={{
          background: 'var(--color-surface-background)',
          borderRadius: 'var(--radius-lg)',
          width: '100%',
          maxWidth: 960,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 'calc(100vh - 4rem)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: 'var(--spacing-4) var(--spacing-6)',
            borderBottom: '1px solid var(--color-surface-border)',
            gap: 'var(--spacing-3)',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <h2
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                fontFamily: 'var(--font-family-sans)',
                margin: 0,
              }}
            >
              {t('devStandardsViewer.diff.modalHeading')}
            </h2>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-muted)',
                marginTop: 'var(--spacing-1)',
                fontFamily: 'var(--font-family-mono)',
              }}
            >
              {fromLabel} <span style={{ color: 'var(--color-text-muted)' }}>→</span> {toLabel}
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            {/* Mode toggle */}
            <div
              role="group"
              aria-label={t('devStandardsViewer.diff.modeToggle')}
              style={{ display: 'flex', border: '1px solid var(--color-surface-border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}
            >
              <button
                type="button"
                aria-pressed={mode === 'unified'}
                onClick={() => setMode('unified')}
                style={{
                  padding: 'var(--spacing-1) var(--spacing-3)',
                  fontSize: 'var(--font-size-xs)',
                  fontFamily: 'var(--font-family-sans)',
                  background: mode === 'unified' ? 'var(--color-brand-primary)' : 'transparent',
                  color: mode === 'unified' ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                {t('devStandardsViewer.diff.unified')}
              </button>
              <button
                type="button"
                aria-pressed={mode === 'sidebyside'}
                onClick={() => setMode('sidebyside')}
                style={{
                  padding: 'var(--spacing-1) var(--spacing-3)',
                  fontSize: 'var(--font-size-xs)',
                  fontFamily: 'var(--font-family-sans)',
                  background: mode === 'sidebyside' ? 'var(--color-brand-primary)' : 'transparent',
                  color: mode === 'sidebyside' ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                {t('devStandardsViewer.diff.sideBySide')}
              </button>
            </div>

            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              aria-label={t('devStandardsViewer.diff.close')}
              style={{
                padding: 'var(--spacing-1) var(--spacing-3)',
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'var(--font-family-sans)',
                background: 'transparent',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                color: 'var(--color-text-secondary)',
              }}
            >
              {t('devStandardsViewer.diff.close')}
            </button>
          </div>
        </div>

        {/* Body */}
        <div
          id="diff-modal-body"
          style={{
            padding: 'var(--spacing-6)',
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {/* Loading */}
          {isLoading && (
            <div
              aria-busy="true"
              style={{
                background: 'var(--color-surface-muted)',
                borderRadius: 'var(--radius-md)',
                height: 200,
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
          )}

          {/* Error */}
          {!isLoading && isError && (
            <p style={{ color: 'var(--color-danger)', fontFamily: 'var(--font-family-sans)', fontSize: 'var(--font-size-sm)' }}>
              {t('devStandardsViewer.diff.loadError')}
            </p>
          )}

          {/* Content */}
          {!isLoading && !isError && fromQuery.data && toQuery.data && (
            <>
              {/* Section change summary */}
              {sectionChanges && <SectionChangeSummary changes={sectionChanges} />}

              {/* Diff view */}
              {mode === 'unified' ? (
                <UnifiedDiffView
                  fromText={fromQuery.data.markdownText}
                  toText={toQuery.data.markdownText}
                />
              ) : (
                <SideBySideDiffView
                  fromText={fromQuery.data.markdownText}
                  toText={toQuery.data.markdownText}
                />
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}
